import { createRouter, createWebHistory } from "vue-router";
import { useAuth, useOidcStore } from "vue3-oidc";

import atmoRoutes from "@/router/atmo";
import atmoMobileRoutes from "@/router/atmo-mobile";
import { useProjectsStore } from "@/store/modules/projects";
import { showWarningNotification } from "@/composables/useNotifications";
import { offlineMissionRedirect } from "@/composables/network/offlineMode";
import { i18nGlobal as i18n } from "@/setup/i18n";
import {
  isDynamicImportError,
  reloadAfterStaleBuild,
  reportStaleBuild,
} from "@/utils/staleBuild";

const routes =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? atmoMobileRoutes
    : atmoRoutes;

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, from) => {
  const redirect = offlineMissionRedirect(to);
  if (!redirect) return undefined;
  if (from.matched.length && to.params.projectId !== redirect.params.projectId)
    showWarningNotification(i18n.t("onlyOfflineMission"));
  return redirect;
});

router.beforeEach(async (to, from, next) => {
  if (!navigator.onLine) {
    next();
    return;
  }

  const { signinRedirect } = useAuth();
  const { state, actions } = useOidcStore();
  if (!state.value.user) {
    const storedUser = await state.value.userManager?.getUser();
    if (storedUser) actions.value.setUser(storedUser);
  }

  if (state.value.user) {
    next();
    return;
  }

  if (to.meta.isPublic) {
    next();
    return;
  }

  void signinRedirect({ state: { redirectPath: to.fullPath } });
  next(false);
});

router.onError(async (error, to) => {
  if (!isDynamicImportError(error)) {
    if (import.meta.env.DEV) console.error(error);
    return;
  }

  if (!navigator.onLine) {
    showWarningNotification(i18n.t("pageUnavailableOffline"));
    return;
  }

  reportStaleBuild({
    reason: "route-import",
    routeName: String(to.name ?? "unknown"),
    error,
  });

  if (useProjectsStore().isOfflineState) return;

  await reloadAfterStaleBuild(router.resolve(to).href);
});

export default router;
